import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { BottomNavigation } from 'enrollment/components';
import {
  DecisionIQDentalSurvey,
  DecisionIQDoctorFrequency,
  DecisionIQInformation,
  DecisionIQMedicalSurvey,
  DecisionIQVisionSurvey,
} from './components';
import {
  useBenefitCategoryQuery,
  useEnrollmentFamilyQuery,
  useEnrollmentProfileQuery,
} from 'enrollment/queries';
import { Loader } from 'theSource/components';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanYearId,
  selectHasQuestionnaire,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { useConfigurationQuery } from 'theSource/queries';
import './DecisionIQ.css';
import { queryKeyGenerator } from 'theSource/utils';
import { ThemedHeader } from 'components/ThemedHeader';

export const DecisionIQ = () => {
  const { t } = useTranslation('pages.decisionIQ');
  const navigate = useNavigate();
  const enrollmentContext = useSelector(selectEnrollmentContext);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContext === 'life_event' ? orgPlanYearContextId : 0;

  const hasQuestionnaire = useSelector(selectHasQuestionnaire);
  const completePages = ['profile', 'family'];
  hasQuestionnaire && completePages.push('questionnaire');

  const { configuration, isLoadingConfiguration } = useConfigurationQuery();
  const { dependentsData, isLoadingDependents, isFetchingDependents } =
    useEnrollmentFamilyQuery({
      lumosEnabled: true,
      manualEnrollmentContextOverride: enrollmentContext,
    });

  const {
    data: benefits,
    isLoading: isLoadingBenefits,
    isFetching: isFetchingBenefits,
  } = useBenefitCategoryQuery({
    key: queryKeyGenerator(
      `init-benefits-${enrollmentContext}`,
      Number(orgPlanYearContextId)
    ),
    enrollmentContextType: enrollmentContext,
    enabled: true,
    lumosApiHost: String(configuration?.apiHost),
    getParamCategory: false,
    includeEligibleDependentsData: true,
    orgPlanYearId: orgPlanYearContextId,
  });

  const { subscriber, isLoadingSubscriber, isFetchingSubscriber } =
    useEnrollmentProfileQuery({ lumosEnabled: true });

  const medicalBenefit = benefits?.data?.benefits?.find((benefit) => {
    return benefit.benefit.benefit_lookup_code === 'medical';
  });
  const medicalBenefitId = medicalBenefit
    ? medicalBenefit.benefit.org_benefit_id
    : 0;

  const dentalBenefit = benefits?.data?.benefits?.find((benefit) => {
    return benefit.benefit.benefit_lookup_code === 'dental';
  });
  const dentalBenefitId = dentalBenefit
    ? dentalBenefit.benefit.org_benefit_id
    : 0;

  const visionBenefit = benefits?.data?.benefits?.find((benefit) => {
    return benefit.benefit.benefit_lookup_code === 'vision';
  });
  const visionBenefitId = visionBenefit
    ? visionBenefit.benefit.org_benefit_id
    : 0;

  const handleNextPage = () => {
    setTimeout(() => navigate('/enrollment/benefits'), 50);
  };

  const isLoading =
    isLoadingDependents ||
    isFetchingDependents ||
    isLoadingConfiguration ||
    isLoadingBenefits ||
    isFetchingBenefits ||
    isLoadingSubscriber ||
    isFetchingSubscriber;

  if (isLoading) {
    return <Loader />;
  }
  
  return (
    <div
      id="decisionIQ"
      className="mt-4 pt-2_5 px-4 pb-4 pt-md-5 px-md-0 pt-lg-0 mt-md-5 mt-lg-4_5"
    >
      <ThemedHeader cssClass="mt-lg-0">{t('title')}</ThemedHeader>
      <div className="card">
        <div className="border-bottom-up">
          <DecisionIQInformation />
        </div>
        <div className="personalize">
          <DecisionIQDoctorFrequency
            dependents={dependentsData}
            subscriber={subscriber?.data}
            orgBenefitId={medicalBenefitId}
          />
        </div>
        {medicalBenefitId > 0 && (
          <DecisionIQMedicalSurvey
            orgBenefitId={medicalBenefitId}
            dependents={dependentsData}
            subscriber={subscriber?.data}
            onSubmit={handleNextPage}
            standalonePage
          />
        )}
        {dentalBenefitId > 0 && (
          <DecisionIQDentalSurvey
            orgBenefitId={dentalBenefitId}
            dependents={dependentsData}
            subscriber={subscriber?.data}
            standalonePage
          />
        )}
        {visionBenefitId > 0 && (
          <DecisionIQVisionSurvey
            orgBenefitId={visionBenefitId}
            dependents={dependentsData}
            subscriber={subscriber?.data}
            standalonePage
          />
        )}
      </div>
      <BottomNavigation
        completePages={completePages}
        onPage="decisioniq"
        translatedPage="pages.decisionIQ"
        onClickEvent={handleNextPage}
      />
    </div>
  );
};
